import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Flavor Cards — add to cart
 *
 * Mirrors the add-to-cart contract used by the theme's own product-form.js:
 *   1. dispatch a real CartLinesUpdateEvent carrying a deferred promise
 *   2. POST to cart/add.js (with `sections` so the drawer HTML re-renders)
 *   3. refresh cart-items-component
 *   4. resolve the deferred promise with { cart, detail }
 *
 * cart-icon.js and cart-drawer.js both hang all of their work off
 * `event.promise`. Firing a plain CustomEvent (cart:updated / cart:refresh /
 * cart:change) leaves that promise undefined, so the bubble never updates and
 * the drawer never re-renders. That was the bug.
 */

const INITIALISED = new WeakSet();
const RESET_DELAY = 2000;
const ERROR_RESET_DELAY = 2500;

/**
 * Re-fetches cart state, preferring the theme's own component so the drawer
 * markup is re-rendered rather than just the raw JSON being read.
 *
 * customElements.whenDefined() never rejects and never times out, so awaiting
 * it hangs forever whenever the element is in the DOM but its module never
 * registers. customElements.get() answers the same question synchronously —
 * no await, no waiting, and no way to stall. If the component isn't ready we
 * go straight to the plain cart fetch on the same tick.
 * @param {string} cartUrl
 */
async function refreshCart(cartUrl) {
  const cartItemsComponent = document.querySelector('cart-items-component');

  if (
    cartItemsComponent &&
    customElements.get('cart-items-component') &&
    typeof cartItemsComponent.fetchCartData === 'function'
  ) {
    return cartItemsComponent.fetchCartData();
  }

  const response = await fetch(`${cartUrl}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/** Section IDs the Section Rendering API should re-render on add. */
function collectSectionIds() {
  const ids = [];
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  }
  return ids.join(',');
}

/**
 * @param {HTMLElement} card
 * @param {Record<string, string>} config
 */
function initCard(card, config) {
  const priceEl = card.querySelector('[data-fc-price]');
  const errorEl = /** @type {HTMLElement | null} */ (card.querySelector('[data-fc-error]'));
  const atcBtn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-fc-atc]'));
  const variantBtns = /** @type {NodeListOf<HTMLButtonElement>} */ (card.querySelectorAll('[data-fc-variant]'));

  const clearError = () => {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  };

  /** @param {string} message */
  const showError = (message) => {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  };

  for (const btn of variantBtns) {
    btn.addEventListener('click', () => {
      for (const b of variantBtns) {
        b.classList.remove('is-active');
        b.setAttribute('aria-pressed', 'false');
      }
      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');

      if (priceEl && btn.dataset.price) priceEl.textContent = btn.dataset.price;
      if (!atcBtn) return;

      clearError();
      atcBtn.dataset.selectedId = btn.dataset.variantId ?? '';

      const available = btn.dataset.available === 'true';
      atcBtn.disabled = !available;
      atcBtn.textContent = available ? config.labelAdd : config.labelSoldout;
    });
  }

  if (!atcBtn) return;

  const initiallyAvailable = !atcBtn.disabled;

  /**
   * Availability of the variant currently selected on this card. Cards with no
   * variant picker fall back to the state Liquid rendered them in.
   */
  const isAvailable = () => {
    const active = /** @type {HTMLElement | null} */ (card.querySelector('[data-fc-variant].is-active'));
    if (active) return active.dataset.available === 'true';
    return initiallyAvailable;
  };

  atcBtn.addEventListener('click', async () => {
    if (atcBtn.disabled) return;

    const id = atcBtn.dataset.selectedId;
    if (!id) return;

    clearError();
    atcBtn.disabled = true;

    const deferred = CartLinesUpdateEvent.createPromise();

    atcBtn.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: id, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    /**
     * Never re-enable a variant that is out of stock — doing so lets the
     * customer click into the same failure indefinitely.
     * @param {number} delay
     */
    const resetSoon = (delay) => {
      setTimeout(() => {
        const available = isAvailable();
        atcBtn.textContent = available ? config.labelAdd : config.labelSoldout;
        atcBtn.disabled = !available;
      }, delay);
    };

    try {
      const response = await fetch(config.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections: collectSectionIds(),
        }),
      });

      const data = await response.json();

      // Shopify returns a `status` field only on failure (e.g. sold out).
      if (data.status) {
        const cart = await refreshCart(config.cartUrl);

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            itemCount: 1,
            source: 'flavor-cards',
          },
        });

        showError(data.description || data.message || config.labelError);
        atcBtn.textContent = config.labelError;
        resetSoon(ERROR_RESET_DELAY);
        return;
      }

      const cart = await refreshCart(config.cartUrl);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          itemCount: 1,
          source: 'flavor-cards',
          sections: data.sections,
        },
      });

      atcBtn.textContent = config.labelAdded;
      resetSoon(RESET_DELAY);
    } catch (error) {
      deferred.reject(error);
      showError(config.labelError);
      atcBtn.textContent = config.labelError;
      resetSoon(ERROR_RESET_DELAY);
    }
  });
}

/** @param {HTMLElement} root */
function initSection(root) {
  if (INITIALISED.has(root)) return;
  INITIALISED.add(root);

  const config = {
    cartAddUrl: root.dataset.cartAddUrl ?? '/cart/add.js',
    cartUrl: root.dataset.cartUrl ?? '/cart',
    labelAdd: root.dataset.labelAdd ?? '',
    labelAdded: root.dataset.labelAdded ?? '',
    labelSoldout: root.dataset.labelSoldout ?? '',
    labelError: root.dataset.labelError ?? '',
  };

  for (const card of root.querySelectorAll('.fc-card')) {
    initCard(/** @type {HTMLElement} */ (card), config);
  }
}

/** @param {ParentNode} [scope] */
function initAll(scope) {
  for (const root of (scope ?? document).querySelectorAll('[data-flavor-cards]')) {
    initSection(/** @type {HTMLElement} */ (root));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
} else {
  initAll();
}

// Theme editor re-renders sections in place.
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof Element) initAll(target);
});
